/** Platform Console copy (namespace `console`). */
const en = {
  nav: {
    overview: "Overview",
    organisation: "Organisation and access",
    lifecycle: "Lifecycle and AI",
    content: "Content and standards",
    assurance: "Assurance",
    tenants: "Tenants and branding",
    identity: "Users and identity",
    roles: "Roles and permissions",
    workflows: "Workflow Studio",
    agents: "Agent Studio",
    prompts: "Prompt library",
    toolkit: "Toolkit and schemas",
    templates: "Document templates",
    conformance: "Conformance registry",
    knowledge: "Knowledge library",
    aiGovernance: "AI governance",
    audit: "Audit and security",
    releases: "Releases",
    backoffice: "Open National Policy AI",
  },
  noAccess: "Your role does not include Platform Console access. Sign in with a platform administration account, or return to National Policy AI.",
  pages: {
    overview: { title: "Platform Console", description: "Configuration and assurance of the National Policy AI platform.", empty: "Choose an area to manage", emptyBody: "Use the menu to configure tenants, identity, workflows, agents, content standards and AI governance." },
    tenants: { title: "Tenants and branding", description: "Ministries, GSOC and the platform tenant, with their branding, domains and status.", empty: "No tenants to show", emptyBody: "Create a ministry workspace to onboard a new entity." },
    identity: { title: "Users and identity", description: "Platform users and their status, and the single sign-on providers (UAE PASS, OpenID Connect).", empty: "No identity providers configured", emptyBody: "Connect UAE PASS or an OpenID Connect provider to enable single sign-on." },
    roles: { title: "Roles and permissions", description: "Built-in roles for each kind of workspace and the permissions they grant.", empty: "No roles", emptyBody: "Roles are defined by the platform." },
    workflows: { title: "Workflow Studio", description: "National lifecycle templates: stages, gates, SLAs and approvals, versioned and published with four-eyes approval.", empty: "No lifecycle templates", emptyBody: "Create a template, validate it and publish it to ministries." },
    agents: { title: "Agent Studio", description: "Research agents, their AI tier, prompt, tools and data-classification ceiling.", empty: "No agents configured", emptyBody: "Agents are loaded from the registry pack." },
    prompts: { title: "Prompt library", description: "Versioned agent and skill prompts: compare versions, draft a new one and activate it.", empty: "No prompts yet", emptyBody: "Prompts are loaded from the registry pack and versioned here." },
    toolkit: { title: "Toolkit and schemas", description: "The registry pack in use, and the record types, fields and enumerations used by forms and agents.", empty: "No record types", emptyBody: "The record-type registry could not be read from the pack." },
    templates: { title: "Document templates", description: "Cabinet Guidebook and Regulations Lab document templates from the registry pack.", empty: "No templates", emptyBody: "No document template matches this framework." },
    conformance: { title: "Conformance registry", description: "Every Guidebook and Regulations Lab requirement, its stage, gate, level and approved wording." },
    knowledge: { title: "Knowledge library", description: "Legislation, guidance and strategies the agents cite: collections, documents, retrieval testing and the glossary." },
    aiGovernance: { title: "AI governance", description: "AI usage and cost, guardrail events, evaluation results and the human review ledger." },
    audit: { title: "Audit and security", description: "The tamper-evident audit trail across workspaces, hash-chain verification and export.", empty: "No events match", emptyBody: "Change the filters or the date range." },
    releases: { title: "Releases", description: "What is deployed: the console build, the API registry pack and platform health." },
  },
  table: { actions: "Actions" },
  form: {
    required: "This field is required.",
    format: "The format is not valid.",
    email: "Enter a valid email address.",
  },
  json: {
    notObject: "The document must be a JSON object.",
    invalid: "Invalid JSON: {{message}}",
    valid: "Valid JSON",
  },
  diff: {
    changes_one: "{{count}} line changed",
    changes_other: "{{count}} lines changed",
    identical: "The two versions are identical.",
  },
  period: {
    label: "Period",
    days_one: "Last {{count}} day",
    days_other: "Last {{count}} days",
  },
  overview: {
    health: "Platform health",
    dbUp: "Database reachable",
    dbDown: "Database unreachable",
    checkedAt: "Checked {{time}}",
    kpi: {
      tenants: "Tenants",
      users: "Users",
      policies: "Policies",
      runsRunning: "Agent runs in progress",
      runsFailed: "Failed agent runs",
      outbox: "Outbox pending",
      notifications: "Notifications pending",
      bridge: "Bridge batches (24 h)",
    },
    last24h: "Last 24 hours",
    bridgeRejected_one: "{{count}} rejected",
    bridgeRejected_other: "{{count}} rejected",
    tenantsTitle: "Tenants",
    tenantsBody: "Workspaces by kind and status.",
    manageTenants: "Manage tenants",
    usageTitle: "AI usage",
    usageBody: "Calls by agent over the last {{days}} days.",
    openGovernance: "Open AI governance",
    areas: "Console areas",
  },
  usage: {
    calls: "AI calls",
    cost: "Cost (USD)",
    emptyTitle: "No AI usage in this period",
    emptyBody: "Usage appears once agents run.",
    byTenant: "Cost by tenant",
    byAgent: "By agent",
    byClassification: "Calls by classification",
    byUser: "By person",
    automated: "Automated work",
    allWorkspaces: "All workspaces",
    workspace: "Workspace",
    computeShared: "This workspace runs on the shared national routes.",
    computePool: "This workspace runs on its dedicated compute pool {{pool}}.",
    avgLatency: "Average latency {{value}} ms",
    ms: "{{value}} ms",
    col: { name: "Name", workspace: "Workspace", tokens: "Tokens", latency: "Average latency", failure: "Failure rate" },
    platformAgents: { shared: { translation: "Machine translation", bilingual: "Bilingual generation", dictation: "Voice dictation", agenda: "Agenda setting" } },
  },
  tenants: {
    kinds: { ministry: "Ministries", oversight: "Oversight (GSOC)", platform: "Platform" },
    statuses: { active: "Active", onboarding: "Onboarding", suspended: "Suspended" },
    storage: { shared: "Shared database", dedicated: "Dedicated database" },
    col: { workspace: "Workspace", kind: "Kind", status: "Status", members: "Members", policies: "Policies", domains: "Email domains", created: "Created" },
    field: {
      kind: "Kind of workspace",
      code: "Code",
      nameEn: "Name (English)",
      nameAr: "Name (Arabic)",
      shortNameEn: "Short name (English)",
      shortNameAr: "Short name (Arabic)",
      sector: "Sector",
      accent: "Accent colour",
      domains: "Email domains",
      adminEmail: "Administrator email",
      adminNameEn: "Administrator name (English)",
      adminNameAr: "Administrator name (Arabic)",
      storage: "Storage",
      status: "Status",
      aiCeiling: "AI processing ceiling",
      computePool: "AI compute",
    },
    policiesValue: "{{active}} active of {{total}}",
    create: "Create ministry workspace",
    created: "Workspace created.",
    saved: "Tenant settings saved.",
    statusSaved: "Tenant status updated.",
    search: "Search tenants",
    noSector: "No sector",
    settingsFor: "Settings for {{name}}",
    settingsBody: "Names, sector, branding and domains of this workspace.",
    gate: "Sign-in gate",
    gateHint: "Share this link with the ministry: it signs people in with UAE PASS or the ministry's directory and opens only this workspace.",
    suspendFor: "Suspend {{name}}",
    activateFor: "Activate {{name}}",
    suspendTitle: "Suspend {{name}}?",
    activateTitle: "Activate {{name}}?",
    suspendBody: "Members of this ministry will no longer be able to sign in to its workspace. Policies and records are kept.",
    activateBody: "Members of this workspace will be able to sign in again.",
    suspend: "Suspend",
    activate: "Activate",
    onlyMinistrySuspend: "Only ministry workspaces can be suspended.",
    classificationTitle: "Classification settings",
    aiCeilingHint: "The highest classification this workspace allows AI to process. It can only tighten the national rule ({{national}}); applied now: {{effective}}.",
    followNational: "Follow the national rule",
    sharedCompute: "Shared national routes",
    computePoolHint: "A dedicated pool runs all of this workspace's AI work on its own compute, such as a ministry's GPU pool or the General Secretariat's GPU unit. Nothing falls back to the shared routes, and web research is off.",
    noComputePools: "No dedicated compute pools are configured on this platform.",
    computePoolMissing: "{{pool}} (not configured)",
    modulesTitle: "Optional modules",
    modules: {
      reglab: "Regulations Lab",
      reglabHint: "Pilots, the RegLab branch of the lifecycle and the RegLab office. Off by default: GSOC runs RegLab as a separate portfolio. Pilots already running stay visible on their policies.",
    },
    wizard: {
      title: "Create ministry workspace",
      description: "Set up the workspace, its branding and its first administrator.",
      steps: { identity: "Identity", branding: "Branding", admin: "Administrator", review: "Review" },
      codeHint: "2 to 16 capital letters or digits, e.g. MOHAP.",
      codeFormat: "Use 2 to 16 capital letters or digits.",
      accentHint: "Hex colour used in the workspace header, e.g. #1B6B5C.",
      accentFormat: "Enter a hex colour such as #1B6B5C.",
      domainsHint: "Separate domains with commas, e.g. mohap.gov.ae.",
      adminIntro: "Optional: add the first administrator of the workspace. They receive the workspace administrator role.",
      reviewNote: "The workspace follows the published national lifecycle templates. You can change its settings afterwards.",
      submit: "Create workspace",
    },
  },
  identity: {
    tabs: { users: "Users", providers: "Identity providers" },
    users: {
      col: { name: "User", status: "Status", mfa: "Multi-factor", lastLogin: "Last sign-in", tenants: "Workspaces" },
      statuses: { active: "Active", disabled: "Disabled" },
      mfaOn: "Enrolled",
      mfaOff: "Not enrolled",
      never: "Never",
      you: "You",
      search: "Search by name or email",
      emptyTitle: "No users match",
      emptyBody: "Change the search or the workspace filter.",
      disable: "Disable",
      enable: "Enable",
      disableTitle: "Disable {{name}}?",
      enableTitle: "Enable {{name}}?",
      disableBody: "The user is signed out and can no longer sign in to any workspace.",
      enableBody: "The user will be able to sign in again with their existing roles.",
      statusSaved: "User status updated.",
    },
    providers: {
      intro: "Single sign-on providers. Secrets are write-only: stored values are never shown, and leaving a secret empty keeps the stored value.",
      add: "Add provider",
      newTitle: "New identity provider",
      editTitle: "Edit {{name}}",
      dialogBody: "Connection settings of the provider. Keys that contain “secret”, “password” or “key” are treated as secrets.",
      saved: "Identity provider saved.",
      enabled: "Enabled",
      disabled: "Disabled",
      workspaceScoped: "Workspace only",
      allWorkspaces: "All workspaces",
      noConfig: "No settings.",
      secretHidden: "Stored (hidden)",
      field: { alias: "Alias", displayName: "Display name (English)", displayNameAr: "Display name (Arabic)", kind: "Protocol", scope: "Workspace", enabled: "Enabled for sign-in" },
      aliasHint: "Lower-case identifier used in the sign-in URL, e.g. uaepass.",
      kinds: { oidc: "OpenID Connect", uaepass: "UAE PASS", saml: "SAML", ldap: "LDAP" },
      types: { text: "Text", list: "List", boolean: "Yes/no", number: "Number", json: "JSON" },
      rowErrors: { duplicate: "This key is used twice.", number: "Enter a number.", json: "Enter valid JSON." },
      config: "Settings",
      configHint: "One row per setting. Lists are comma-separated.",
      key: "Key",
      type: "Type",
      value: "Value",
      listHint: "Comma-separated values",
      secretValue: "Secret value for {{key}}",
      secretStored: "Stored — type to replace",
      secretNew: "Enter the secret",
      removeRow: "Remove {{key}}",
      addRow: "Add setting",
    },
  },
  roles: {
    kindFilter: "Kind of workspace",
    allKinds: "All kinds",
    searchLabel: "Permission",
    searchPlaceholder: "Search permissions",
    summary: "{{roles}} roles · {{permissions}} permissions",
    emptyTitle: "Nothing matches",
    emptyBody: "Change the kind or the search.",
    caption: "Permissions granted by each role",
    permission: "Permission",
    granted: "Granted",
    notGranted: "Not granted",
    customNote: "Roles are built in and defined per kind of workspace. Tenant-defined roles are not supported yet.",
    groups: {
      policies: "Policies",
      records: "Records",
      proposals: "AI proposals",
      versions: "Versions",
      comments: "Comments",
      gates: "Gates",
      conformance: "Conformance",
      agents: "Agents",
      consultation: "Consultation",
      monitoring: "Monitoring",
      reglab: "Regulations Lab",
      members: "Members",
      workspace: "Workspace",
      templates: "Templates",
      oversight: "Oversight",
      signals: "Signals",
      knowledge: "Knowledge",
      academy: "Academy",
      audit: "Audit",
      console: "Platform Console",
    },
  },
  permissions: {
    policies: { read: "Read policies", create: "Create policies", edit: "Edit policies", delete: "Delete policies", share: "Share policies", disclose: "Disclose classified content" },
    records: { edit: "Edit stage records" },
    proposals: { review: "Review AI proposals" },
    versions: { edit: "Edit policy versions" },
    comments: { write: "Write comments" },
    gates: { decide: "Decide gates", submit: "Submit to gates" },
    conformance: { attest: "Attest requirements", waive: "Waive requirements" },
    agents: { run: "Run agents" },
    consultation: { manage: "Manage consultations", respond: "Respond to consultations" },
    monitoring: { edit: "Edit monitoring" },
    reglab: { manage: "Manage Regulations Lab pilots" },
    members: { manage: "Manage members" },
    workspace: { settings: "Workspace settings" },
    templates: { manage: "Manage workspace templates" },
    oversight: { pipeline: "View national pipeline", review: "Review submissions", decide: "Decide submissions", cabinet: "Cabinet agenda", registry: "Manage national registry", overlaps: "Overlap analysis", reglab: "Regulations Lab oversight", analytics: "National analytics", governance: "National governance settings" },
    signals: { read: "Read signals", manage: "Manage signals" },
    knowledge: { read: "Read knowledge library", manage: "Manage knowledge library" },
    academy: { use: "Use the Academy", manage: "Manage the Academy" },
    audit: { read: "Read audit trail" },
    console: { tenants: "Console: tenants", identity: "Console: identity", roles: "Console: roles", workflows: "Console: workflows", agents: "Console: agents and prompts", registry: "Console: registry", aigov: "Console: AI governance", audit: "Console: audit", knowledge: "Console: knowledge", releases: "Console: releases" },
  },
  workflows: {
    statuses: { draft: "Draft", published: "Published", retired: "Retired" },
    scopes: { builtin: "Built-in", national: "National", workspace: "Workspace" },
    tabs: { stages: "Stages", gates: "Gates", recordTypes: "Record types", conformance: "Conformance", json: "JSON" },
    phases: { phase1: "Phase 1 · Development", phase2: "Phase 2 · Consultation and approval", pmo: "GSOC review and Cabinet decision", phase3: "Phase 3 · Implementation" },
    owners: { ministry: "Ministry", pmo: "GSOC", cabinet: "Cabinet" },
    modes: { off: "Off", warn: "Warn", block: "Block" },
    col: { template: "Template", status: "Status", scope: "Scope", instruments: "Instruments", stages: "Stages", policies: "Policies", approved: "Approved" },
    new: "New template",
    search: "Search templates",
    back: "All templates",
    version: "Version",
    versionOption: "Version {{version}} · {{status}}",
    editDraft: "Edit draft",
    newDraft: "New draft version",
    publish: "Publish",
    publishTitle: "Publish {{name}} version {{version}}?",
    publishBody: "Ministries starting new policies will use this version. National templates need a second administrator: you cannot publish a draft you saved yourself.",
    published: "Template published.",
    retire: "Retire",
    retireTitle: "Retire {{name}} version {{version}}?",
    retireBody: "The version can no longer be chosen for new policies. Policies already using it continue on it.",
    retired: "Template retired.",
    fourEyes: "Four-eyes rule: a national draft must be published by an administrator other than the one who saved it.",
    issuesTitle: "Validation issues",
    issuesCount_one: "{{count}} issue",
    issuesCount_other: "{{count}} issues",
    valid: "Valid",
    changeNote: "Change note",
    pilotPhases: "Pilot phases",
    editor: {
      newTitle: "New lifecycle template",
      newVersionTitle: "New version of {{name}}",
      description: "Edit the lifecycle graph as JSON. The API validates it and lists any issues; a draft can be saved with issues but not published.",
      key: "Key",
      keyHint: "Lower-case letters, digits and hyphens, e.g. national-default.",
      keyFormat: "Use lower-case letters, digits and hyphens (2 to 60 characters).",
      copyFrom: "Start from",
      copyFromHint: "Copy the graph of an existing template.",
      blank: "Empty graph",
      nameEn: "Name (English)",
      nameAr: "Name (Arabic)",
      descriptionEn: "Description (English)",
      descriptionAr: "Description (Arabic)",
      instruments: "Instrument types",
      changeNote: "Change note",
      changeNoteHint: "What changed in this version, for the approver.",
      graph: "Lifecycle graph",
      saveDraft: "Save draft",
      savedWithIssues: "Draft version {{version}} saved with issues",
      openDraft: "Open draft",
    },
    graph: {
      stage: "Stage",
      phase: "Phase",
      owner: "Owner",
      sla: "SLA",
      calendarDays_one: "{{count}} calendar day",
      calendarDays_other: "{{count}} calendar days",
      workingDays_one: "{{count}} working day",
      workingDays_other: "{{count}} working days",
      leadAgent: "Lead agent",
      recordTypes: "Record types",
      gate: "Gate",
      approvers: "Approvers",
      outcomes: "Outcomes",
      requiredRecords: "Required records",
      checks: "Checks",
      stepUp: "Step-up",
      bilingual: "Bilingual",
      noPending: "No pending proposals",
      minConfidence: "Confidence ≥ {{value}}",
      effects: "Effects",
      noRecordTypes: "No record types in this template.",
      required: "required",
      noConformance: "No gate evaluates conformance.",
    },
  },
  agents: {
    col: { agent: "Agent", team: "Team", tier: "AI tier", maxClass: "Classification ceiling", status: "Status", runs: "Runs (30 days)", acceptance: "Acceptance" },
    tiers: { Reasoning: "Reasoning", Fast: "Fast", Arabic: "Arabic", Vision: "Vision" },
    teams: { orchestrator: "Orchestrator", phase1: "Phase 1 · Ministry", phase2: "Phase 2 · Ministry", pmo: "GSOC", phase3: "Phase 3 · Monitoring", shared: "Shared" },
    allStages: "Every stage",
    moreStages: "+{{count}} more",
    teamNumber: "{{team}} · agent {{number}}",
    on: "Enabled",
    off: "Disabled",
    enabled: "Enabled",
    configure: "Configure {{name}}",
    search: "Search agents",
    stages: "Stages",
    tools: "Tools",
    tierHint: "The AI capability tier the agent runs on.",
    maxClassHint: "The highest classification of data the agent may read.",
    prompt: "Prompt",
    reviewLocked: "Human review is always required",
    reviewLockedBody: "Agent output is a proposal until an officer accepts it; this cannot be switched off.",
    saved: "Agent saved.",
  },
  prompts: {
    search: "Search prompts",
    active: "active",
    activeVersion: "Version {{version}} · {{date}}",
    versionLabel: "Version {{version}}",
    newVersion: "New version",
    newTitle: "New version of {{key}}",
    newBody: "The new version is saved inactive unless you activate it now.",
    contentEn: "Prompt (English)",
    contentAr: "Prompt (Arabic)",
    changeNote: "Change note",
    changeNoteHint: "What changed and why, for the reviewer.",
    activateNow: "Activate this version now",
    activationNeedsGovernance: "Activation requires AI governance approval; the version is saved inactive.",
    saveVersion: "Save version",
    created: "Prompt version saved.",
    activate: "Activate",
    activateTitle: "Activate {{key}} version {{version}}?",
    activateBody: "Agents will use this version from their next run.",
    activated: "Prompt version activated.",
    approvedAt: "Approved {{time}}",
    notApproved: "Not approved yet",
    tabs: { text: "Text", diff: "Compare" },
    from: "From",
    to: "To",
  },
  toolkit: {
    pack: "Registry pack",
    version: "Version",
    requirements: "Requirements",
    recordTypes: "Record types",
    families: "Requirement families",
    templates: "Document templates",
    sha: "Pack checksum (SHA-256)",
    search: "Search record types",
    col: { type: "Record type", module: "Module", fields: "Fields", required: "Required fields" },
    field: "Field",
    kind: "Kind",
    constraints: "Constraints",
  },
  templates: {
    sections_one: "{{count}} section",
    sections_other: "{{count}} sections",
    tabular: "Table",
  },
  conformance: {
    framework: "Framework",
    allFrameworks: "All frameworks",
    frameworks: { uae_cabinet_guidebook: "Cabinet Policy Guidebook", uae_reglab: "Regulations Lab" },
    stages: {
      principles: "Principles",
      rationale: "Rationale",
      options_impacts: "Options and impacts",
      consultation: "Consultation",
      implementation: "Implementation",
      evaluation: "Evaluation",
      reglab_general: "Regulations Lab · general",
      reglab_application: "Regulations Lab · application",
      reglab_permit: "Regulations Lab · permit",
      reglab_testing: "Regulations Lab · testing",
      reglab_impact: "Regulations Lab · impact",
      reglab_review: "Regulations Lab · review",
    },
    gates: {
      submission: "Submission",
      publication: "Publication",
      in_force: "Entry into force",
      none: "No gate",
      pilot_permit: "Pilot · permit",
      pilot_testing_reporting: "Pilot · testing and reporting",
      pilot_impact_assessment: "Pilot · impact assessment",
      pilot_review: "Pilot · review",
      pilot_decision: "Pilot · decision",
    },
    levels: { mandatory: "Mandatory", recommended: "Recommended" },
    arSources: { official: "Official Arabic", decoded_unverified: "Arabic awaiting sign-off" },
    col: { id: "Reference", wording: "Requirement", family: "Family", stage: "Stage", gate: "Gate", level: "Level", arabic: "Arabic wording" },
    search: "Search requirements",
    emptyTitle: "No requirements match",
    emptyBody: "Change the filters or the search.",
    attestation: "Needs attestation",
    automated: "Automated check",
    wordingEn: "English wording",
    wordingAr: "Arabic wording",
    anchors: "Where it is assessed",
    source: "Source",
    openSource: "Open source document",
    page: "Guidebook page {{page}}",
    signoff: {
      statuses: { official: "Official Arabic", pending: "Awaiting GSOC sign-off", approved: "Signed off by GSOC", changes_requested: "Changes requested" },
      summary: "Arabic wording decoded from the Guidebook: {{approved}} of {{total}} signed off, {{pending}} awaiting sign-off, {{changes}} with changes requested.",
      summaryHint: "Until GSOC signs off its Arabic wording, a Guidebook requirement only warns at its gate. Once signed off, it blocks the gate when unmet.",
      title: "GSOC sign-off of the Arabic wording",
      explain: "Compare the Arabic with the Arabic edition of the Guidebook. Any later change to the wording needs a new sign-off.",
      correctedAr: "Corrected Arabic (optional)",
      correctedArHint: "Leave empty to sign off the Arabic as shown. A corrected wording replaces it on every policy.",
      note: "Note",
      noteHint: "Required when you request changes: say what needs to change.",
      approve: "Sign off",
      requestChanges: "Request changes",
      saved: "Sign-off saved",
      by: "{{name}}, {{date}}",
      approveFamily: "Sign off {{count}} awaiting in this family",
      approveFamilyTitle: "Sign off {{count}} Arabic wordings?",
      approveFamilyBody: "The Arabic wording of every requirement awaiting sign-off in {{family}} is signed off as shown, and each one then blocks its gate when unmet.",
      approvedCount: "{{count}} wordings signed off",
      pmoOnly: "Only GSOC users can sign off the Guidebook's Arabic wording.",
      correctedShown: "Corrected Arabic in use",
    },
  },
  knowledge: {
    tabs: { documents: "Documents", search: "Retrieval test", glossary: "Glossary" },
    split: "{{national}} national · {{workspace}} workspace",
    col: { title: "Document", collection: "Collection", kind: "Kind", scope: "Scope", classification: "Classification", chunks: "Passages", added: "Added" },
    national: "National",
    workspace: "Workspace",
    allCollections: "All collections",
    searchDocs: "Search documents",
    emptyDocs: "No documents",
    emptyDocsBody: "Change the filters, or upload a document.",
    legalStatus: "Legal status",
    openSource: "Open source",
    chunks_one: "{{count}} indexed passage",
    chunks_other: "{{count}} indexed passages",
    noChunks: "Not indexed yet",
    noChunksBody: "Passages appear once indexing has finished.",
    deactivate: "Remove from library",
    deactivateTitle: "Remove this document?",
    deactivateBody: "“{{title}}” will no longer be searched or cited by agents. Existing citations are kept.",
    deactivated: "Document removed from the library.",
    searchPlaceholder: "Ask a question or enter keywords",
    run: "Search",
    searchIntro: "Test retrieval",
    searchIntroBody: "Runs the same hybrid search the agents use and shows the passages and scores they would receive.",
    noHits: "No passages found",
    noHitsBody: "Try other words or another collection.",
    results: "Search results",
    score: "Score {{score}}",
    searchGlossary: "Search terms",
    emptyGlossary: "No terms",
    emptyGlossaryBody: "No glossary term matches the search.",
    upload: "Upload document",
    uploadTitle: "Upload a document",
    uploadBody: "The document is stored and indexed in the background, then becomes available to agents.",
    fileHint: "PDF, Word, text, Markdown or HTML.",
    uploaded: "Document uploaded; indexing has started.",
    publishNational: "Publish to the national library (visible to every workspace)",
    field: {
      file: "File",
      titleEn: "Title (English)",
      titleAr: "Title (Arabic)",
      issuer: "Issuer",
      number: "Number",
      year: "Year",
      sourceUrl: "Source link",
      summaryEn: "Summary (English)",
      summaryAr: "Summary (Arabic)",
      sectors: "Sectors",
    },
  },
  aigov: {
    tabs: { usage: "Usage and cost", guardrails: "Guardrails", evals: "Evaluations", ledger: "Review ledger" },
    severity: { Info: "Information", Warning: "Warning", Blocked: "Blocked" },
    metrics: { accuracy: "Accuracy", citationFaithfulness: "Citation faithfulness", schemaValidity: "Schema validity", arabicQuality: "Arabic quality" },
    col: { at: "Time", severity: "Severity", agent: "Agent", rule: "Rule", detail: "Detail", action: "Action taken", tenant: "Workspace", prompt: "Prompt version", cases: "Cases", drift: "Drift", trigger: "Trigger" },
    allSeverities: "All severities",
    allAgents: "All agents",
    emptyGuardrails: "No guardrail events",
    emptyGuardrailsBody: "No guardrail was triggered in this period.",
    emptyEvals: "No evaluation runs",
    emptyEvalsBody: "Evaluation runs appear after an agent or prompt change is evaluated.",
    drift: "Drift",
    stable: "Stable",
    trend: "Evaluation trend · {{agent}} (%)",
    emptyLedger: "No reviews in this period",
    emptyLedgerBody: "The ledger records how officers disposed of each AI proposal.",
    ledger: {
      title: "Human review of AI output",
      body: "How officers disposed of each agent's proposals.",
      accepted: "Accepted",
      edited: "Edited",
      rejected: "Rejected",
      rate: "Acceptance rate",
      editDistance: "Average edit distance",
    },
  },
  audit: {
    export: "Export CSV",
    filters: "Audit filters",
    search: "Search summary, actor or entity",
    from: "From date",
    to: "To date",
    allTenants: "All workspaces",
    clear: "Clear filters",
    platform: "Platform",
    col: { at: "Time", tenant: "Workspace", actor: "Actor", action: "Action", entity: "Entity", summary: "Summary" },
    actorKinds: { human: "Person", agent: "AI agent", system: "System" },
    classification: "Classification",
    ip: "IP address",
    correlation: "Correlation id",
    hash: "Event hash",
    verifyTitle: "Hash-chain verification",
    verifyBody: "Recomputes the hash chain of each workspace's audit trail to prove no event was altered or removed.",
    verify: "Verify chains",
    reverify: "Verify again",
    intact: "Intact",
    broken: "Broken",
    intactTitle: "All chains are intact",
    intactBody_one: "{{count}} chain verified.",
    intactBody_other: "{{count}} chains verified.",
    brokenTitle_one: "{{count}} chain is broken",
    brokenTitle_other: "{{count}} chains are broken",
    brokenBody: "An event no longer matches its hash. Preserve the database and report a security incident.",
    brokenAt: "Breaks at event {{id}}",
    events_one: "{{count}} event",
    events_other: "{{count}} events",
  },
  releases: {
    noHistoryTitle: "Release history is not available yet",
    noHistoryBody: "The API has no release or deployment endpoint. This page shows what is running now.",
    console: "Platform Console",
    consoleBody: "This build of the console.",
    builtAt: "Built",
    mode: "Mode",
    modes: { development: "Development", production: "Production", test: "Test" },
    api: "API",
    checkedAt: "Checked {{at}}",
    pack: "Registry pack",
    database: "Database",
  },
};

export default en;
export type ConsoleCatalog = typeof en;
